import React from "react";
import { useRecoilValue } from 'recoil';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { Search } from '@mui/icons-material';

import { isDebug } from "../../utils";
import { userInfo } from "../../auth/AuthProvider";

import ContentLayout from "../../layouts/ContentLayout";
import { ControlWriteMember, ControlDataGrid, Columns4Member, ControlSnackbar, ControlCircularProgressBar, Dialog4Confirm, Dialog4Alert, UserType } from "../../components/board";
import { useMemberListQuery, useMemberItemPutMutation } from "../../hooks/useBoard/useMemberQuery";


export default function Manage4Member () {
    const navigate = useNavigate();
    const auth = useRecoilValue(userInfo);
    const { t } = useTranslation();
    const isLoadingRef = React.useRef(false);
    const refConfirm = React.useRef(null);
    const refAlert = React.useRef(null);
    const refSnackbar = React.useRef(null);
    const showTimerControler = React.useRef(null);

    const [ pageTitle] = React.useState(t('nav.manage4member'));
    const { mutate: putMemberItem } = useMemberItemPutMutation();

    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });
    const defaultQueryOptions = {
        Page: paginationModel.page + 1,
        PageSize: paginationModel.pageSize,
        MemberType: "",
        MemberName: "",
        CompanyName: "",
        RegNo: "",
        Email: "",
    };
    const [queryOptions, setQueryOptions] = React.useState(defaultQueryOptions);
    const [ dataSet, setDataSet ] = React.useState({
        records: [],
        rowLength: 0,
        editable: false,
        isLoading: false,
    });
    const [ isUseUserType, setIsUseUserType ] = React.useState(false);
    const [selectedElements, setSelectedElements] = React.useState(UserType);

    const { isError, isLoading, isFetching, isSuccess, error, data: gridData, refetch } = useMemberListQuery(queryOptions);
    
    const [openDetailDialog, setOpenDetailDialog] = React.useState({isOpen: false, meberId: null, regFailYn: 'Y'}); // 등록 창


    const closeDetailDialog = (boolValue) => {
        if(boolValue) {
            refetch();
        }
        if(openDetailDialog) setOpenDetailDialog({isOpen: false, meberId: null, regFailYn: 'Y'});
    };

    const runSearch = (e) => {
        e.preventDefault();
        
        if (isLoadingRef.current)  return;
        isLoadingRef.current = true;
        showTimerControler.current.showTimer(2);
        refetch();
        isLoadingRef.current = false;
    };

    const handleApproval = (memberid, membername) => {
        if((auth.userId === undefined || auth.userId === null || auth.userId === '') || auth?.userType !== process.env.REACT_APP_BOARD_MANAGER_TYPE) {
            refSnackbar.current.openMessage(`${t('error.notlogin')}`);
            setTimeout(() => {
                navigate(`/login`);
            }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
        }
        else {
            if(memberid === undefined || memberid === null || memberid === '' ) 
            {
                return;
            }
            if (isLoadingRef.current)  return;
            isLoadingRef.current = true;
            refConfirm.current.showMessageBox(`${t('button.confirm')}`, `[${membername}] [${memberid}] ${t('alert.approve')}`, (result) => {
                if(result) {
                    putMemberItem({MemberId: memberid, ApprovalYn: "Y"}, {
                        onSuccess: () => {
                            refAlert.current.showMessageBox(`${t('button.confirm')}`, `${t('message.approved')}`);
                            runSearch(null);
                        },
                    });
                }
            });
            isLoadingRef.current = false;
        }
    };

    React.useEffect(() => {
        try {
            if(gridData?.response_code === 201)
            {
                setDataSet({...dataSet, records: gridData.data.Results, rowLength: gridData.data.TotalCnt});
            }
            else if (gridData?.message !== null && gridData?.message?.length > 0)
            {
                refAlert?.current?.showMessageBox(`${t('error.title')}`, JSON.stringify(gridData.message));
            }
            else if(isSuccess === false && (error !== undefined || error !== null || JSON.stringify(error) !== null ))
            {
                if (isDebug()) console.log("QueryBind[MLF]", new Date().toISOString(), error);
            }
        }
        catch(err) {
            console.log("No Data[MLF]", new Date().toISOString(), err);
        }

        return () => {
            setDataSet({
                records: [],
                rowLength: 0,
                editable: false,
                isLoading: false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, isLoading, isFetching, isSuccess, error, gridData]);
    
    React.useEffect(() => { 
        if (isDebug()) console.log("## UseEffect [paginationModel] paginationModel: ", new Date().toISOString(), JSON.stringify(paginationModel));
        setQueryOptions({...queryOptions, Page: paginationModel.page + 1, PageSize: paginationModel.pageSize});
        if (isDebug()) console.log("## UseEffect [paginationModel] queryOptions: ", new Date().toISOString(), JSON.stringify(queryOptions));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel]);


    return (
        <ContentLayout pageTitle={pageTitle} pageDescription={null} >
        
            <div className="page-description">
                <h1>사용자 승인 관리</h1>
                <p>※ 신규 가입한 사용자를 로그인 가능한 사용자로 승인을 처리합니다.</p>
            </div>

            {/* 검색 영역 */}
            <Container className="page-content" sx={{ p: 3 }}>
                <Grid container spacing={1}>
                    {isUseUserType ? <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={{ paddingRight: '10px'}} >
                        <FormControl sx={{ m: 0, p: 0, minWidth: 200, width: "100%" }} size="small">
                            <InputLabel id="member-type-select-small-label">{t('field.membertype')}</InputLabel>
                            <Select
                                labelId="member-type-select-small-label"
                                id="member-type-select-small"
                                value={queryOptions.MemberType  || ''}
                                label={t('field.membertype')}
                                onChange={(v) => setQueryOptions({...queryOptions, MemberType:v.target.value}) }
                                size="small"
                            >
                                <MenuItem value="">
                                    <em>{t('searchfield.searchall')}</em>
                                </MenuItem>
                                {selectedElements ? selectedElements?.map(item => {
                                    return <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>
                                }) : null
                                }
                            </Select>
                        </FormControl>
                    </Grid> : null}
                    <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={{ paddingRight: '10px'}} >
                        <TextField
                            fullWidth
                            label={t('searchfield.membername')}
                            id="filled-hidden-label-small"
                            defaultValue={queryOptions.MemberName}
                            onChange={(v) => setQueryOptions({...queryOptions, MemberName:v.target.value}) }
                            size="small"
                            placeholder={t('searchfield.compay_placeholder')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={{ paddingRight: '10px'}} >
                        <TextField
                            fullWidth
                            label={t('searchfield.compay')}
                            id="filled-hidden-label-small"
                            defaultValue={queryOptions.CompanyName}
                            onChange={(v) => setQueryOptions({...queryOptions, CompanyName:v.target.value}) }
                            size="small"
                            placeholder={t('searchfield.compay_placeholder')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 2 }} sx={{ paddingRight: '10px'}} >
                        <TextField
                            fullWidth
                            label={t('searchfield.regno')}
                            id="filled-hidden-label-small"
                            defaultValue={queryOptions.RegNo}
                            onChange={(v) => setQueryOptions({...queryOptions, RegNo:v.target.value}) }
                            size="small"
                            placeholder={t('searchfield.email_placeholder')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 3 }} sx={{ paddingRight: '10px'}} >
                        <TextField
                            fullWidth
                            label={t('searchfield.email')}
                            id="filled-hidden-label-small"
                            defaultValue={queryOptions.Email}
                            onChange={(v) => setQueryOptions({...queryOptions, Email:v.target.value}) }
                            size="small"
                            placeholder={t('searchfield.email_placeholder')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 1 }} sx={{ textAlign: 'right' }} >
                        <div>
                            <Button variant="outlined" size="medium" startIcon={<Search />} onClick={runSearch}>
                                {t('button.search')}
                            </Button>
                        </div>
                    </Grid>
                </Grid>
            </Container>

            {/* 데이타 영역 */}
            <Container sx={{ width: '100%', height: '100%', alignItems: 'center', p: 1 }}>
                <ControlDataGrid t={t}
                    dataType="user"
                    manageId="memberlist"
                    boardColumns={Columns4Member}
                    dataSet={dataSet}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    isUseManager={true}
                    handleApproval={handleApproval}
                    onCellClick={(params) => {
                        if (params.field === 'MemberName' || params.field === 'Email' || params.field === 'MemberId') {    
                            const memberId = params.row.MemberId;
                            const regFailYn = params.row.RegFailYn || 'Y';
                            if (memberId) {
                                setOpenDetailDialog({isOpen: true, meberId: memberId, regFailYn: regFailYn});
                            }
                        }
                    }}
                />
            </Container>

            {/* 알림 메시지 영역 */}
            <Dialog4Alert ref={refAlert} />
            <Dialog4Confirm ref={refConfirm} />
            <ControlSnackbar ref={refSnackbar} />
            <ControlCircularProgressBar ref={showTimerControler} initialTime={1} />
            <Typography>&nbsp;</Typography>

            {/* 사용자 정보 영역 */}
            <ControlWriteMember id="detailMember" memberId={openDetailDialog.meberId} open={openDetailDialog.isOpen} closeEditDialog={closeDetailDialog} regFailYn={openDetailDialog.regFailYn} handleApproval={handleApproval} refMessage={showTimerControler.current} />

        </ContentLayout>
    );
}